import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import {
  getNotifications,
  getUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
} from '../api-wa/notificationsApi';
import { useConfig } from './WaConfigContext';

export const NotificationContext = createContext(null);

// ── Avatar colour palette (consistent per contact) ───────────────────────────
const AVATAR_COLORS = [
  '#7C4DFF', '#00BCD4', '#FF7043', '#4CAF50',
  '#E91E63', '#FF9800', '#2196F3', '#9C27B0',
];

export const getAvatarColor = (name = '') => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

export const getInitials = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');

// ── Relative-time formatter ───────────────────────────────────────────────────
export const timeAgo = (iso) => {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60)   return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

// ── Provider ──────────────────────────────────────────────────────────────────
export const NotificationProvider = ({ children, setActiveView }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount]     = useState(0);
  const [toasts, setToasts]               = useState([]);
  const [isSocketConnected, setIsSocketConnected] = useState(false);

  const wsRef          = useRef(null);
  const reconnectTimer = useRef(null);
  const reconnectDelay = useRef(1000);

  const { activeConfigId } = useConfig();

  // ── Initial fetch ───────────────────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token || !activeConfigId) return;

    // Defer notification fetching so it doesn't compete with the initial critical rendering path
    const timer = setTimeout(() => {
      Promise.all([getNotifications(), getUnreadCount()])
        .then(([notifs, count]) => {
          // Filter out read notifications so only unread ones are shown and read ones are cleared
          const unreadNotifs = notifs.filter(n => !n.is_read);
          setNotifications(unreadNotifs);
          setUnreadCount(count);
        })
        .catch(() => {});
    }, 1000);

    return () => clearTimeout(timer);
  }, [activeConfigId]);

  // ── WebSocket connection ────────────────────────────────────────────────────
  const connect = useCallback(() => {
    const token = localStorage.getItem('authToken');
    if (!token || !activeConfigId) return;

    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const wsHost = isLocal ? 'localhost:8000' : `hrmbackendapi.${window.location.hostname.replace('hrm.', '')}`;
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${wsProtocol}//${wsHost}/ws/notifications/${activeConfigId}/`);
    wsRef.current = ws;

    ws.onopen = () => {
      reconnectDelay.current = 1000; // reset backoff
      setIsSocketConnected(true);
      // keepalive ping every 25s
      ws._pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 25000);
    };

    ws.onmessage = (evt) => {
      try {
        const data = JSON.parse(evt.data);
        handleWsEvent(data);
      } catch (_) {}
    };

    ws.onclose = () => {
      setIsSocketConnected(false);
      clearInterval(ws._pingInterval);
      // Exponential back-off reconnect
      reconnectTimer.current = setTimeout(() => {
        reconnectDelay.current = Math.min(reconnectDelay.current * 1.5, 30000);
        connect();
      }, reconnectDelay.current);
    };

    ws.onerror = () => {
      setIsSocketConnected(false);
      ws.close();
    };
  }, [activeConfigId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    connect();
    return () => {
      clearTimeout(reconnectTimer.current);
      if (wsRef.current) {
        wsRef.current.onclose = null; // prevent reconnect on unmount
        wsRef.current.close();
      }
    };
  }, [connect]);

  // ── Handle incoming WS events ───────────────────────────────────────────────
  const handleWsEvent = (data) => {
    if (data.whatsapp_config_id && String(data.whatsapp_config_id) !== String(activeConfigId)) {
        return; // Extra safety: ignore events for other configs
    }

    if (data.event === 'new_notification') {
      const notif = data.notification;
      setNotifications((prev) => {
        // Deduplicate by id
        if (prev.some((n) => n.id === notif.id)) return prev;
        return [notif, ...prev].slice(0, 50);
      });
      setUnreadCount(data.unread_count ?? 0);
      // Show toast
      setToasts((prev) => [...prev, { ...notif, toastId: Date.now() }]);
    }

    if (data.event === 'unread_count_update') {
      setUnreadCount(data.count ?? 0);
    }
  };

  // ── Mark single as read ─────────────────────────────────────────────────────
  const markRead = useCallback(async (id) => {
    try {
      await markNotificationRead(id);
      // Clear (remove) the notification from the list immediately upon marking read
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (_) {}
  }, []);

  // ── Mark all as read ────────────────────────────────────────────────────────
  const markAllRead = useCallback(async () => {
    try {
      await markAllNotificationsRead();
      const currentNotifications = notifications;
      setNotifications((prev) => prev.map(n => ({...n, is_read: true})));
      setUnreadCount(0);
      window.dispatchEvent(new CustomEvent('notificationsMarkedRead', { detail: currentNotifications }));
    } catch (_) {}
  }, [notifications]);

  // ── Clear all ────────────────────────────────────────────────────────
  const clearAll = useCallback(async () => {
    try {
      await markAllNotificationsRead();
      setNotifications([]);
      setUnreadCount(0);
    } catch (_) {}
  }, []);

  // ── Dismiss a toast ─────────────────────────────────────────────────────────
  const dismissToast = useCallback((toastId) => {
    setToasts((prev) => prev.filter((t) => t.toastId !== toastId));
  }, []);

  // ── Open chat from notification ─────────────────────────────────────────────
  const openChat = useCallback(
    (notif) => {
      if (notif?.id) markRead(notif.id);
      if (notif?.contact_phone) {
        localStorage.setItem('pendingInboxPhone', notif.contact_phone);
        // Dispatch custom event to select the contact immediately if inbox_view.jsx is already open
        window.dispatchEvent(new CustomEvent('pendingNotificationChat', { detail: notif.contact_phone }));
      }
      if (setActiveView) setActiveView('inbox');
    },
    [markRead, setActiveView]
  );

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        toasts,
        markRead,
        markAllRead,
        clearAll,
        dismissToast,
        openChat,
        isSocketConnected,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used inside <NotificationProvider>');
  return ctx;
};

